import { useTranslation, Translation } from 'react-i18next';
import React, { useCallback, useMemo, useState } from 'react';
import { login } from '@/utils';
import { WiderTable, Textarea, Button, HelperTooltip, Loading } from '@/components';
import { Modal } from '../../Modal';
import { getAppealColumns } from './columns';
import style from './index.module.css';
import cx from 'clsx';
import { useSpamDetail, useSpamAppealList, useMarkUserSpam, useSubmitAppeal } from './hooks';
import { IconRcAccountCommentIcon } from '@/assets';
import { IconCheckedOption, IconQuestion } from '@/assets/spam';
import { useCurrentAddressInfo, useSwitch, invalidUserInfo } from '@/hooks';

const { confirm, CommonModal } = Modal;

export type SpamReportModalProp = {
  address: string;
  refetchCallback?: () => void;
};

const now = new Date();
const Date7DaysAgo = now.setDate(now.getDate() - 7);

const showAppealModal = ({
  refetchAll,
  mutateAsyncSubmitAppeal,
  placeholder,
}: {
  refetchAll(): void;
  mutateAsyncSubmitAppeal: any;
  placeholder: string;
}) => {
  let comment = '';
  const setComment = (value: string) => {
    comment = value;
  };

  confirm({
    title: <Translation>{t => t('SpamReport.s2q589')}</Translation>,
    modalClassName: style.appealModalClassName,

    content: (
      <Textarea
        placeholder={placeholder}
        onChange={v => setComment(v || '')}
        rows={2}
        maxLength={128}
      />
    ),
    confirmBtnText: <Translation>{t => t('SpamReport.submit')}</Translation>,
    showCancelBtn: false,
  }).then(() => {
    mutateAsyncSubmitAppeal({ comment }).then(() => {
      refetchAll();
    });
  });
};

export const MarkSpamModal = ({
  address,
  isSpamDefault,
  turnOff,
  isOpen,
  refetchAll,
}: {
  address: string;
  turnOff(): void;
  isOpen: boolean;
  isSpamDefault?: boolean;
  refetchAll(): void;
}) => {
  const { t } = useTranslation();

  const [isSpam, setIsSpam] = useState(isSpamDefault || false);
  const { mutateAsync: mutateAsyncMarkSpam } = useMarkUserSpam();
  const config = useMemo(
    () => [
      {
        label: t('SpamReport.1bu9xb'),
        value: true,
      },
      {
        label: t('SpamReport.jcqw8v'),
        value: false,
      },
    ],
    [t],
  );
  const submitHandler = useCallback(async () => {
    if (isSpamDefault === isSpam) {
      turnOff();
      return;
    }
    await mutateAsyncMarkSpam({ id: address, isSpam });
    refetchAll();
    turnOff();
  }, [address, isSpam, isSpamDefault, mutateAsyncMarkSpam, refetchAll, turnOff]);
  return (
    <CommonModal onRequestClose={turnOff} on={isOpen}>
      <div className={style.markSpamModalContainer}>
        <div className={style.header}>{t('SpamReport.pqkwxl')}</div>
        <div className={style.optionSelect}>
          {config.map((item, index) => (
            <div
              className={cx(style.option, item.value === isSpam && style.activeOption)}
              onClick={() => setIsSpam(item.value)}
              key={index}>
              {item.label}
              <img className={style.checkedIcon} src={IconCheckedOption} alt="checked" />
            </div>
          ))}
        </div>
        <div className={style.tip}>
          {isSpam ? t('SpamReport.otherSpam') : t('SpamReport.otherNoSpam')}
        </div>
        <Button type="primary" className={style.confirmButton} onClick={submitHandler}>
          {t('SpamReport.179d1nk')}
        </Button>
      </div>
    </CommonModal>
  );
};

const SpamLabel = ({
  isSpam,
  noLabel,
  isLoading,
}: {
  isSpam?: boolean;
  isLoading: boolean;
  noLabel?: boolean;
}) => {
  const { t } = useTranslation();

  return isLoading ? (
    <Loading />
  ) : (
    <span className={cx(style.spamLabel, noLabel && isSpam === null && style.noValue)}>
      {noLabel && isSpam === null
        ? t('SpamReport.noLabel')
        : isSpam
          ? t('SpamReport.sybil')
          : t('SpamReport.non-sybil')}
    </span>
  );
};
export const SpamReportModal = ({ address, refetchCallback }: SpamReportModalProp) => {
  const { t } = useTranslation();

  const { address: myAddress, isVerified } = useCurrentAddressInfo();
  const isMyAddress = useMemo(() => myAddress === address, [address, myAddress]);
  const {
    data: spamAppealList,
    isFetched: isSpamAppealListFetched,
    refetch: refetchSpamAppealList,
  } = useSpamAppealList({ enabled: isMyAddress });
  const { mutateAsync: mutateAsyncSubmitAppeal } = useSubmitAppeal();

  const appealColumns = useMemo(() => getAppealColumns(), []);
  const {
    on: onMarkSpamModal,
    turnOff: turnOffMarkSpamModal,
    turnOn: turnOnMarkSpamModal,
  } = useSwitch(false);
  const {
    data: spamDetail,
    isLoading: isSpamDetailLoading,
    refetch: refetchSpamDetail,
  } = useSpamDetail(address);

  const refetchAll = useCallback(() => {
    refetchSpamDetail();
    refetchSpamAppealList();
    invalidUserInfo(address, myAddress);
    refetchCallback && refetchCallback();
  }, [address, myAddress, refetchCallback, refetchSpamAppealList, refetchSpamDetail]);

  const handleSubmitComplaint = useCallback(() => {
    if (!isVerified) {
      login();
      return;
    }

    showAppealModal({ refetchAll, mutateAsyncSubmitAppeal, placeholder: t('SpamReport.reasons') });
  }, [isVerified, mutateAsyncSubmitAppeal, refetchAll, t]);

  const showAppealButton = useMemo(
    () => isMyAddress && spamDetail?.platform,
    [isMyAddress, spamDetail?.platform],
  );
  const couldSubmitAppeal = useMemo(() => {
    const lastAppeal = isSpamAppealListFetched && spamAppealList?.appeal_list[0];
    if (lastAppeal) {
      if (lastAppeal.audit_result === null) {
        return false;
      }
      if (lastAppeal?.create_at * 1000 > Date7DaysAgo) {
        return false;
      }
    }
    return showAppealButton;
  }, [isSpamAppealListFetched, showAppealButton, spamAppealList?.appeal_list]);
  const finalResultTooltip = useMemo(() => {
    const isSpam = spamDetail?.final;
    if (isMyAddress) {
      return isSpam ? t('SpamReport.isspam') : t('SpamReport.nospam');
    } else {
      return isSpam ? t('SpamReport.otherSpam') : t('SpamReport.otherNoSpam');
    }
  }, [isMyAddress, spamDetail?.final, t]);

  const handleOpenMarkSpamModal = useCallback(() => {
    if (isMyAddress) {
      return;
    }
    if (!isVerified) {
      login();
      return;
    }
    turnOnMarkSpamModal();
  }, [isMyAddress, isVerified, turnOnMarkSpamModal]);

  return (
    <div className={style.container}>
      <div className={style.targetAddress}>
        {t('SpamReport.48kket')}
        <span className={style.address}>{address}</span>
      </div>
      <div className={style.addressDetail}>
        <div className={style.column}>
          <div className={style.columnTitle}>{t('SpamReport.1u18yvl')}</div>
          <div className={style.columnTitle}>{t('SpamReport.flc63t')}</div>
          <div className={style.columnTitle}>{t('SpamReport.13qb01w')}</div>
        </div>
        <div className={style.column}>
          <div className={style.columnValue}>
            <SpamLabel isSpam={spamDetail?.platform} isLoading={isSpamDetailLoading} />
          </div>
          <div className={style.columnValue}>
            <SpamLabel isSpam={spamDetail?.personal} noLabel isLoading={isSpamDetailLoading} />{' '}
            <HelperTooltip
              overlayStyle={{
                visibility: isMyAddress ? 'visible' : 'hidden',
              }}
              title={t('SpamReport.editOwn')}>
              <IconRcAccountCommentIcon
                width={16}
                height={16}
                className={cx(style.markIcon, isMyAddress && style.disabled)}
                onClick={handleOpenMarkSpamModal}
              />
            </HelperTooltip>
          </div>
          <div className={style.columnValue}>
            <SpamLabel isSpam={spamDetail?.final} isLoading={isSpamDetailLoading} />{' '}
            <HelperTooltip title={finalResultTooltip}>
              <img
                src={IconQuestion}
                className={style.questionIcon}
                width={12}
                height={12}
                alt=""
              />
            </HelperTooltip>
          </div>
        </div>
      </div>

      {isMyAddress ? (
        <div className={style.appealList}>
          <div className={style.title}>
            {t('SpamReport.8o7fzg')}
            {showAppealButton ? (
              <HelperTooltip
                overlayStyle={{
                  visibility: couldSubmitAppeal ? 'hidden' : 'visible',
                }}
                title={t('SpamReport.zk8vgh')}>
                <Button
                  type="primary"
                  disabled={!couldSubmitAppeal}
                  className={style.submitAppealButton}
                  onClick={handleSubmitComplaint}>
                  {t('SpamReport.10bzbgk')}
                </Button>
              </HelperTooltip>
            ) : null}
          </div>
          {spamAppealList?.appeal_list?.length ? (
            <WiderTable
              className={style.table}
              columns={appealColumns}
              data={spamAppealList?.appeal_list}
              bodyHeight={200}
            />
          ) : isSpamAppealListFetched ? (
            <div className={style.noDataTip}>{t('SpamReport.rvba6m')}</div>
          ) : (
            <Loading />
          )}
        </div>
      ) : null}
      {onMarkSpamModal ? (
        <MarkSpamModal
          turnOff={turnOffMarkSpamModal}
          isOpen={onMarkSpamModal}
          address={address}
          isSpamDefault={spamDetail?.personal}
          refetchAll={refetchAll}
        />
      ) : null}
    </div>
  );
};
